import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { DealMilestone, DealStage } from "@/pages/deals/types";
import {
  useDealStages,
  useUpdateDealStage,
  useUpdateDealStagesOrder
} from "@/entities/settings/deal/hooks";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import { getMilestoneIcon } from "./StageDetailedView";
import toast from "react-hot-toast";
import UnsavedChangesInDrawerModal from "@/pages/settings/components/UnsavedChangesInDrawerModal";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  getMilestoneOptions,
  getPositionOptions
} from "@/pages/settings/components/dealsSettings/StageStudio/CreateNewStage";
import { confirmationModalType } from "@/pages/settings/components/dealsSettings/StageStudio/StageStudioTab";
import { calculateNewStageOrder } from "@/pages/settings/components/shared/stageStudio/calculateNewStageOrder";
import { StagePosition } from "@/pages/settings/components/shared/stageStudio/types";

const getCurrentStagePosition = (
  currentStage: DealStage,
  allStages: DealStage[]
): string => {
  const currentIndex = allStages.findIndex(
    (stage) => stage.id === currentStage.id
  );

  if (currentIndex === -1) {
    // Stage not found in the milestone, default to "last"
    return StagePosition.LAST;
  }

  if (currentIndex === 0) return StagePosition.FIRST;
  if (currentIndex === allStages.length - 1) return StagePosition.LAST;

  const previousStage = allStages[currentIndex - 1];
  return `${StagePosition.AFTER_PREFIX}${previousStage.id}`;
};

const TRANSLATION_KEY =
  "settings.deals.stageStudio.stageDetailedView.editStageDrawer";

export interface EditStageFormData {
  name: string;
  milestone: DealMilestone;
  positionInPipeline: string;
}

interface EditStageDrawerProps {
  isOpen: boolean;
  onClose: (stageWasUpdated?: boolean) => void;
  stageData: DealStage | null;
}

const EditStageDrawer: React.FC<EditStageDrawerProps> = ({
  isOpen,
  onClose,
  stageData
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateStageMutation = useUpdateDealStage();
  const updateStagesOrderMutation = useUpdateDealStagesOrder();
  const { data: dealStagesData } = useDealStages(true);
  const stages = dealStagesData?.stages || [];

  const formRef = useRef<HTMLFormElement>(null);
  const [formSetValue, setFormSetValue] = useState<
    ((field: string, value: unknown) => void) | null
  >(null);

  const [isFormValid, setIsFormValid] = useState(!!stageData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<{
    formData: EditStageFormData;
    modalType: confirmationModalType;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (isOpen && stageData) {
      setHasUnsavedChanges(false);
    } else if (!isOpen) {
      setHasUnsavedChanges(false);
    }
  }, [isOpen, stageData]);

  const positionOptions = useMemo(() => {
    return getPositionOptions(stages, t, stageData || undefined);
  }, [stages, t, stageData]);

  useEffect(() => {
    if (typeof formSetValue === "function" && stageData) {
      const currentPosition = getCurrentStagePosition(stageData, stages);
      formSetValue("positionInPipeline", currentPosition);
    }
  }, [formSetValue, stageData, stages]);

  const hasActualChanges = (data: EditStageFormData) => {
    if (!stageData) return false;

    const currentPosition = getCurrentStagePosition(stageData, stages);

    return (
      data.name !== stageData.name ||
      data.milestone !== stageData.milestone ||
      data.positionInPipeline !== currentPosition
    );
  };

  const hasPositionChange = (data: EditStageFormData) => {
    if (!stageData) return false;

    const currentPosition = getCurrentStagePosition(stageData, stages);

    return data.positionInPipeline !== currentPosition;
  };

  const getModalType = (data: EditStageFormData): confirmationModalType => {
    if (!stageData) return "moveStageForward";

    const currentIndex = stages.findIndex((stage) => stage.id === stageData.id);

    let newIndex = -1;

    if (data.positionInPipeline === StagePosition.FIRST) {
      newIndex = 0;
    } else if (data.positionInPipeline === StagePosition.LAST) {
      newIndex = stages.length - 1;
    } else if (data.positionInPipeline.startsWith(StagePosition.AFTER_PREFIX)) {
      const targetStageId = parseInt(
        data.positionInPipeline.replace(StagePosition.AFTER_PREFIX, ""),
        10
      );
      const targetIndex = stages.findIndex(
        (stage) => stage.id === targetStageId
      );
      newIndex = targetIndex + 1;
    }

    return newIndex > currentIndex ? "moveStageForward" : "moveStageBackward";
  };

  const requiresConfirmation = (data: EditStageFormData): boolean => {
    if (!stageData || !hasPositionChange(data)) return false;

    const stageHasDeals = (stageData.dealsCount || 0) > 0;
    return stageHasDeals;
  };

  const executeUpdate = async (data: EditStageFormData) => {
    if (!stageData) return;

    if (!hasActualChanges(data)) {
      setHasUnsavedChanges(false);
      onClose();
      return;
    }

    try {
      const { name, milestone, positionInPipeline } = data;
      const creationData = { name, milestone };
      await updateStageMutation.mutateAsync({
        pathParams: { stageId: stageData.id.toString() },
        data: creationData
      });

      if (hasPositionChange(data)) {
        const newOrder = calculateNewStageOrder(
          stages,
          stageData.id,
          positionInPipeline
        );
        await updateStagesOrderMutation.mutateAsync({
          data: newOrder
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["settings/deals/stages/stageById"]
      });

      setHasUnsavedChanges(false);
      toast.success(
        t(`${TRANSLATION_KEY}.successToast`, {
          stageName: data.name
        }),
        {
          duration: 5000
        }
      );
      onClose(true);
    } catch (error) {
      console.error("Failed to update stage:", error);
      toast.error(t(`${TRANSLATION_KEY}.errorToast`));
    }
  };

  const handleSubmit = async (data: EditStageFormData) => {
    if (!stageData) return;

    if (!hasActualChanges(data)) {
      setHasUnsavedChanges(false);
      onClose();
      return;
    }

    if (requiresConfirmation(data)) {
      const modalType = getModalType(data);
      setPendingUpdateData({
        formData: data,
        modalType,
        onConfirm: () => executeUpdate(data)
      });
      setIsConfirmationModalOpen(true);
    } else {
      await executeUpdate(data);
    }
  };

  const handleConfirmReorder = () => {
    if (pendingUpdateData) {
      pendingUpdateData.onConfirm();
      setPendingUpdateData(null);
    }
    setIsConfirmationModalOpen(false);
  };

  const handleCancelReorder = () => {
    if (stageData && typeof formSetValue === "function") {
      const currentPosition = getCurrentStagePosition(stageData, stages);
      formSetValue("positionInPipeline", currentPosition);
    }

    setPendingUpdateData(null);
    setIsConfirmationModalOpen(false);
  };

  const handleSubmitButton = () => {
    formRef.current?.requestSubmit();
  };

  const handleCancel = () => {
    const formData = new FormData(formRef.current!);
    const data: EditStageFormData = {
      name: formData.get("name") as string,
      milestone: parseInt(formData.get("milestone") as string) as DealMilestone,
      positionInPipeline: formData.get("positionInPipeline") as string
    };

    if (hasUnsavedChanges && hasActualChanges(data)) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    onClose();
  };

  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleFormValidation = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  if (!stageData) return null;

  const sections: FormSectionProps[] = [
    {
      id: "name-and-milestone",
      fields: [
        {
          name: "name",
          type: "text",
          required: true,
          label: t(`settings.deals.stageStudio.createStage.name`),
          layout: "flex",
          fieldClassName: "flex-1",
          defaultValue: stageData.name,
          fieldActionProps: {
            placeholder: t(
              `settings.deals.stageStudio.createStage.namePlaceholder`
            ),
            onChange: handleFormChange
          }
        },
        {
          name: "milestone",
          type: "singleSelect",
          required: true,
          label: t(`settings.deals.stageStudio.createStage.milestone`),
          layout: "flex",
          fieldClassName: "flex-1 min-w-0",
          defaultValue: stageData.milestone,
          hintText: t(
            `settings.deals.stageStudio.createStage.milestoneHelpText`
          ),
          fieldActionProps: {
            options: getMilestoneOptions(t),
            placeholder: t(
              `settings.deals.stageStudio.createStage.chooseMilestone`
            ),
            variant: "form",
            renderIcon: getMilestoneIcon,
            onSelection: handleFormChange
          }
        }
      ],
      fieldsContainerClassName: "flex flex-row gap-6"
    },
    {
      id: "position",
      fields: [
        {
          name: "positionInPipeline",
          type: "singleSelect",
          required: false,
          label: t(`settings.deals.stageStudio.createStage.positionInPipeline`),
          defaultValue: getCurrentStagePosition(stageData, stages),
          fieldActionProps: {
            options: positionOptions,
            placeholder: t(
              `settings.deals.stageStudio.createStage.selectPosition`
            ),
            variant: "form",
            clearable: false,
            onSelection: handleFormChange
          }
        }
      ]
    }
  ];

  return (
    <>
      <Drawer
        open={isOpen}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={false}
        noBodyStyles={true}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <Text variant="lgSubtitle">{t(`${TRANSLATION_KEY}.title`)}</Text>
            </DrawerTitle>
            <IconButton
              data-test="close-stage-drawer"
              onClick={handleCancel}
              variant="iconNoBg"
              size="sm"
            >
              <Close />
            </IconButton>
          </DrawerHeader>
          <div className="p-5 w-full overflow-y-scroll">
            <FormNew
              sections={sections}
              isFormValid={handleFormValidation}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
              shouldNotResetOnSubmit
            />
          </div>

          <DrawerFooter>
            <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                data-test="cancel-button"
                variant="secondaryNoBg"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <Button
                data-test="submit-button"
                disabled={!isFormValid || updateStageMutation.isPending}
                onClick={handleSubmitButton}
              >
                {t("save")}
              </Button>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      <UnsavedChangesInDrawerModal
        isOpen={showUnsavedModal}
        setIsOpen={setShowUnsavedModal}
        handleSubmit={() => {
          setShowUnsavedModal(false);
          handleSubmitButton();
        }}
        handleCancel={handleDiscardChanges}
      />
      {pendingUpdateData && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          title={t(
            `settings.deals.stageStudio.${pendingUpdateData.modalType}.title`
          )}
          confirmationText={
            <div className="flex flex-col gap-4">
              <span>
                {t(
                  `settings.deals.stageStudio.${pendingUpdateData.modalType}.message`
                )}
              </span>
              <span>
                {t(
                  `settings.deals.stageStudio.${pendingUpdateData.modalType}.message2`
                )}
              </span>
            </div>
          }
          handleSubmit={handleConfirmReorder}
          handleCancel={handleCancelReorder}
          isPrimaryButton={true}
          submitButtonText={t(
            `settings.deals.stageStudio.${pendingUpdateData.modalType}.confirm`
          )}
        />
      )}
    </>
  );
};

export default EditStageDrawer;
